import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../module_roots/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faFileInvoice,
    faCamera,
    faSave,
    faTimes,
    faSpinner,
    faTag,
    faImage,
    faUpload,
    faChevronLeft,
    faChevronRight,
    faExclamationTriangle,
    faCheckCircle,
    faExpand,
    faCompress
} from '@fortawesome/free-solid-svg-icons';
import "../module_mechanics/Styles/Form.css";

// ==============================================
// API Endpoint
// ==============================================
const API_BASE = 'https://ourzestapp.online/oz_backend/solar_module';
const INVOICE_API = `${API_BASE}/solar_pendinginvoices.php`;

// ==============================================
// FIX: API Helper to prevent "Unexpected token '<'"
// ==============================================
const fetchApi = async (payload) => {
    const response = await fetch(INVOICE_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
    });

    const contentType = response.headers.get("content-type");
    
    // If the server returns HTML (e.g., 404 Not Found, 500 Server Error, or Firewall block)
    if (!contentType || !contentType.includes("application/json")) {
        const text = await response.text();
        console.error("Server returned non-JSON response (HTML/Error page):", text);
        throw new Error(`Server returned Status ${response.status}. The API file might be missing or PHP crashed. Check console.`);
    }

    return await response.json();
};

// ==============================================
// Translation Utility
// ==============================================
const createInvoiceTranslator = () => {
    let cache = new Map();

    const loadFromCache = () => {
        try {
            const cached = localStorage.getItem('ourzest_invoice_translations');
            if (cached) {
                const parsed = JSON.parse(cached);
                Object.entries(parsed).forEach(([key, value]) => {
                    cache.set(key, value);
                });
            }
        } catch (e) {
            console.log("No cache found in Invoice Form");
        }
    };

    const saveToCache = () => {
        try {
            const cacheObj = {};
            cache.forEach((value, key) => {
                cacheObj[key] = value;
            });
            localStorage.setItem('ourzest_invoice_translations', JSON.stringify(cacheObj));
        } catch (e) {
            console.error("Error saving to cache:", e);
        }
    };

    const translateAPI = async (text, targetLang) => {
        if (targetLang === 'en') return text;

        const dict = {
            'ur': {
                'New Invoice': 'نیا انوائس',
                'Edit Invoice': 'انوائس میں ترمیم کریں',
                'Invoice Number': 'انوائس نمبر',
                'Bill Number': 'بل نمبر',
                'Bill Pictures': 'بل کی تصاویر',
                'Save Invoice': 'انوائس محفوظ کریں',
                'Update Invoice': 'انوائس اپ ڈیٹ کریں',
                'Clear': 'صاف کریں',
                'Loading': 'لوڈ ہو رہا ہے...',
                'Saving': 'محفوظ ہو رہا ہے...',
                'Invoice Saved': 'انوائس کامیابی سے محفوظ ہو گئی!',
                'Invoice Updated': 'انوائس کامیابی سے اپ ڈیٹ ہو گئی!',
                'Failed to save': 'محفوظ کرنے میں ناکامی',
                'Failed to update': 'اپ ڈیٹ کرنے میں ناکامی',
                'Network Error': 'نیٹ ورک کی خرابی',
                'Success': 'کامیابی',
                'Error': 'خرابی',
                'Warning': 'انتباہ',
                'Capture Bill': 'بل کی تصویر لیں',
                'Upload Bill': 'بل اپ لوڈ کریں',
                'Image too large': 'تصویر بہت بڑی ہے'
            },
            'ar': {
                'New Invoice': 'فاتورة جديدة',
                'Edit Invoice': 'تعديل الفاتورة',
                'Invoice Number': 'رقم الفاتورة',
                'Bill Number': 'رقم الفاتورة',
                'Bill Pictures': 'صور الفاتورة',
                'Save Invoice': 'حفظ الفاتورة',
                'Update Invoice': 'تحديث الفاتورة',
                'Clear': 'مسح',
                'Loading': 'جاري التحميل...',
                'Saving': 'جاري الحفظ...',
                'Invoice Saved': 'تم حفظ الفاتورة بنجاح!',
                'Invoice Updated': 'تم تحديث الفاتورة بنجاح!',
                'Failed to save': 'فشل في الحفظ',
                'Failed to update': 'فشل في التحديث',
                'Network Error': 'خطأ في الشبكة',
                'Success': 'نجاح',
                'Error': 'خطأ',
                'Warning': 'تحذير',
                'Capture Bill': 'التقاط الفاتورة',
                'Upload Bill': 'رفع الفاتورة',
                'Image too large': 'الصورة كبيرة جداً'
            }
        };

        if (dict[targetLang] && dict[targetLang][text]) {
            return dict[targetLang][text];
        }

        return text;
    };

    const translateText = async (text, targetLang) => {
        const cacheKey = `${text}_${targetLang}`;

        if (cache.has(cacheKey)) {
            return cache.get(cacheKey);
        }

        const translated = await translateAPI(text, targetLang);
        cache.set(cacheKey, translated);
        saveToCache();

        return translated;
    };

    const defaultTexts = {
        newInvoice: 'New Invoice',
        editInvoice: 'Edit Invoice',
        invoiceNumber: 'Invoice Number',
        billNumber: 'Bill Number',
        billPictures: 'Bill Pictures',
        saveInvoice: 'Save Invoice',
        updateInvoice: 'Update Invoice',
        clear: 'Clear',
        loading: 'Loading...',
        saving: 'Saving...',
        invoiceSaved: 'Invoice saved successfully!',
        invoiceUpdated: 'Invoice updated successfully!',
        failedSave: 'Failed to save invoice',
        failedUpdate: 'Failed to update invoice',
        networkError: 'Network error occurred',
        success: 'Success',
        error: 'Error',
        warning: 'Warning',
        captureBill: 'Capture Bill',
        uploadBill: 'Upload Bill',
        imageTooLarge: 'Image too large'
    };

    const getTranslations = async (lang) => {
        if (lang === 'en') {
            return defaultTexts;
        }

        const translations = {};
        const promises = [];

        Object.keys(defaultTexts).forEach(key => {
            const cacheKey = `${defaultTexts[key]}_${lang}`;
            if (cache.has(cacheKey)) {
                translations[key] = cache.get(cacheKey);
            } else {
                promises.push(
                    translateText(defaultTexts[key], lang).then(translated => {
                        translations[key] = translated;
                    })
                );
            }
        });

        if (promises.length > 0) {
            await Promise.all(promises);
            saveToCache();
        }

        return translations;
    };

    loadFromCache();

    return {
        getTranslations,
        translateText
    };
};

const invoiceTranslator = createInvoiceTranslator();

// ==============================================
// Toast Notification Component
// ==============================================
const ToastNotification = ({ toast, setToast, translations }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success': return faCheckCircle;
            case 'error': return faExclamationTriangle;
            case 'warning': return faExclamationTriangle;
            default: return faCheckCircle;
        }
    };

    const getTitleForType = (type) => {
        switch (type) {
            case 'success': return translations?.success || 'Success';
            case 'error': return translations?.error || 'Error';
            case 'warning': return translations?.warning || 'Warning';
            default: return '';
        }
    };

    if (!toast.show) return null;

    return (
        <div className={`ozui_toast-notification ${toast.type}`}>
            <div className="ozui_toast-content">
                <div className="ozui_toast-icon">
                    <FontAwesomeIcon icon={getIconForType(toast.type)} />
                </div>
                <div className="ozui_toast-text">
                    <div className="ozui_toast-title">{getTitleForType(toast.type)}</div>
                    <div className="ozui_toast-message">{toast.message}</div>
                </div>
                <button className="ozui_toast-close" onClick={() => setToast(prev => ({ ...prev, show: false }))}>
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
        </div>
    );
};

// ==============================================
// Image Compression Helper
// ==============================================
const compressImage = (file, maxWidth = 1200, maxHeight = 1200, quality = 0.75) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (e) => {
            const img = new Image();
            img.onload = () => {
                let width = img.width;
                let height = img.height;

                if (width > maxWidth || height > maxHeight) {
                    const ratio = Math.min(maxWidth / width, maxHeight / height);
                    width = Math.round(width * ratio);
                    height = Math.round(height * ratio);
                }

                const canvas = document.createElement('canvas');
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                ctx.imageSmoothingEnabled = true;
                ctx.imageSmoothingQuality = 'high';
                ctx.drawImage(img, 0, 0, width, height);

                const compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
                resolve(compressedDataUrl);
            };
            img.onerror = reject;
            img.src = e.target.result;
        };
        reader.onerror = reject;
    });
};

// ==============================================
// Bill Pictures Gallery Component
// ==============================================
const BillPicturesGallery = ({ pictures, onRemove, onAdd, translations }) => {
    const [selectedIndex, setSelectedIndex] = useState(null);
    const [touchStartX, setTouchStartX] = useState(0);
    const [touchStartY, setTouchStartY] = useState(0);
    const [scale, setScale] = useState(1);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const fileInputRef = useRef(null);
    const cameraInputRef = useRef(null);
    const viewerRef = useRef(null);

    const handleFileUpload = async (event) => {
        const files = Array.from(event.target.files || []);
        event.target.value = '';
        if (files.length === 0) return;

        const validFiles = files.filter(file => file.size <= 10 * 1024 * 1024);
        if (validFiles.length !== files.length) {
            alert(translations?.imageTooLarge || 'Some images were too large. Please select images under 10MB.');
        }

        for (const file of validFiles) {
            try {
                let compressedImage;
                try {
                    compressedImage = await compressImage(file, 1200, 1200, 0.75);
                } catch (error) {
                    console.error('Error compressing image, retrying:', error);
                    compressedImage = await compressImage(file, 800, 800, 0.5);
                }
                onAdd(compressedImage);
            } catch (error) {
                console.error('Failed to process image:', error);
                alert(`Failed to process ${file.name || 'an image'}. Please try again.`);
            }
        }
    };

    const openImageViewer = (index) => {
        setSelectedIndex(index);
        setScale(1);
        setPosition({ x: 0, y: 0 });
        document.body.style.overflow = 'hidden';
    };

    const closeImageViewer = () => {
        setSelectedIndex(null);
        setScale(1);
        setPosition({ x: 0, y: 0 });
        document.body.style.overflow = '';
    };

    const toggleFullscreen = () => {
        if (!viewerRef.current) return;
        if (!document.fullscreenElement) {
            viewerRef.current.requestFullscreen?.();
        } else {
            document.exitFullscreen?.();
        }
    };

    const navigateImage = (direction) => {
        if (selectedIndex === null) return;
        const newIndex = selectedIndex + direction;
        if (newIndex >= 0 && newIndex < pictures.length) {
            setSelectedIndex(newIndex);
            setScale(1);
            setPosition({ x: 0, y: 0 });
        }
    };

    const handleTouchStart = (e) => {
        const touch = e.touches[0];
        setTouchStartX(touch.clientX);
        setTouchStartY(touch.clientY);
        setIsDragging(true);
        setDragStart({ x: position.x, y: position.y });
    };

    const handleTouchMove = (e) => {
        e.preventDefault();
        if (e.touches.length === 1 && isDragging && scale > 1) {
            const touch = e.touches[0];
            const deltaX = touch.clientX - touchStartX;
            const deltaY = touch.clientY - touchStartY;
            setPosition({
                x: dragStart.x + deltaX,
                y: dragStart.y + deltaY
            });
        }
    };

    const handleTouchEnd = () => {
        setIsDragging(false);
    };

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (selectedIndex !== null) {
                if (e.key === 'ArrowLeft') navigateImage(-1);
                if (e.key === 'ArrowRight') navigateImage(1);
                if (e.key === 'Escape') closeImageViewer();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [selectedIndex, pictures.length]);

    useEffect(() => {
        setScale(1);
        setPosition({ x: 0, y: 0 });
    }, [selectedIndex]);

    return (
        <div className="ozui-bill-gallery">
            <div className="ozui-gallery-grid">
                {pictures.map((pic, index) => (
                    <div key={index} className="ozui-gallery-item">
                        <img
                            src={pic}
                            alt={`Bill ${index + 1}`}
                            onClick={() => openImageViewer(index)}
                            loading="lazy"
                        />
                        <button
                            className="ozui-gallery-remove"
                            onClick={(e) => {
                                e.stopPropagation();
                                onRemove(index);
                            }}
                            type="button"
                        >
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    </div>
                ))}

                <div className="ozui-gallery-add-container">
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleFileUpload}
                        style={{ display: 'none' }}
                    />
                    <input
                        ref={cameraInputRef}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        onChange={handleFileUpload}
                        style={{ display: 'none' }}
                    />
                    <button
                        type="button"
                        className="ozui-gallery-add-btn ozui-gallery-capture-btn"
                        onClick={() => cameraInputRef.current?.click()}
                    >
                        <FontAwesomeIcon icon={faCamera} />
                        <span>{translations?.captureBill || 'Capture Bill'}</span>
                    </button>
                    <button
                        type="button"
                        className="ozui-gallery-add-btn ozui-gallery-upload-btn"
                        onClick={() => fileInputRef.current?.click()}
                    >
                        <FontAwesomeIcon icon={faUpload} />
                        <span>{translations?.uploadBill || 'Upload Bill'}</span>
                    </button>
                </div>
            </div>

            {pictures.length > 0 && (
                <div className="ozui-gallery-info">
                    <span>{pictures.length} {translations?.billPictures || 'pictures'}</span>
                </div>
            )}

            {selectedIndex !== null && pictures[selectedIndex] && (
                <div 
                    className="ozui-image-viewer-overlay" 
                    onClick={closeImageViewer}
                    ref={viewerRef}
                >
                    <div 
                        className="ozui-image-viewer-content" 
                        onClick={(e) => e.stopPropagation()}
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                    >
                        <div className="ozui-image-viewer-controls">
                            <button 
                                className="ozui-image-viewer-close" 
                                onClick={closeImageViewer}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                            <div className="ozui-image-viewer-counter">
                                {selectedIndex + 1} / {pictures.length}
                            </div>
                            <button 
                                className="ozui-image-viewer-fullscreen" 
                                onClick={toggleFullscreen}
                            >
                                <FontAwesomeIcon icon={faExpand} />
                            </button>
                        </div>

                        <div className="ozui-image-viewer-image-wrapper">
                            <img 
                                src={pictures[selectedIndex]} 
                                alt={`Bill ${selectedIndex + 1}`}
                                style={{
                                    transform: `scale(${scale}) translate(${position.x / scale}px, ${position.y / scale}px)`,
                                    transition: isDragging ? 'none' : 'transform 0.2s ease'
                                }}
                                draggable={false}
                            />
                        </div>

                        <div className="ozui-image-viewer-nav">
                            <button
                                className="ozui-image-viewer-nav-btn"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    navigateImage(-1);
                                }}
                                disabled={selectedIndex === 0}
                            >
                                <FontAwesomeIcon icon={faChevronLeft} />
                            </button>
                            <button
                                className="ozui-image-viewer-nav-btn"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    navigateImage(1);
                                }}
                                disabled={selectedIndex === pictures.length - 1}
                            >
                                <FontAwesomeIcon icon={faChevronRight} />
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// ==============================================
// Main Pending Invoices Component
// ==============================================
const PendingInvoices = ({ invoiceId = null, onSuccess = null }) => {
    const { user, currentShop } = useAuth();

    const initialFormData = {
        invoice_number: '',
        customer_name: '',
        bill_number: '',
        bill_pictures: [],
        username: '',
        shop_id: null
    };

    const [formData, setFormData] = useState(initialFormData);
    const [saving, setSaving] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [errors, setErrors] = useState({});
    const [currentLanguage, setCurrentLanguage] = useState(() => localStorage.getItem('ourzest_language') || 'en');
    const [translations, setTranslations] = useState({});
    const [darkMode, setDarkMode] = useState(() => localStorage.getItem('ourzest_darkMode') === 'true');
    const [toast, setToast] = useState({ show: false, type: '', message: '' });
    const [isLoading, setIsLoading] = useState(false);

    const toastTimerRef = useRef(null);
    const isSavingRef = useRef(false);
    const invoiceNumberRef = useRef(null);

    const showToast = useCallback((type, message) => {
        if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        setToast({ show: true, type, message });
        toastTimerRef.current = setTimeout(() => {
            setToast({ show: false, type: '', message: '' });
        }, 3000);
    }, []);

    useEffect(() => {
        return () => {
            if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        };
    }, []);

    // Initialize shop_id from currentShop
    useEffect(() => {
        if (currentShop?.shop_id) {
            setFormData(prev => ({
                ...prev,
                shop_id: currentShop.shop_id,
                username: user?.username || ''
            }));
        }
    }, [currentShop, user]);

    const generateInvoiceNumber = useCallback(async () => {
        if (!currentShop?.shop_id) return;

        try {
            // FIX: Using fetchApi
            const data = await fetchApi({
                action: 'get_next_invoice_number',
                shop_id: currentShop.shop_id,
                username: user?.username
            });

            if (data.status === 'success' && data.data) {
                setFormData(prev => ({
                    ...prev,
                    invoice_number: data.data.invoice_number
                }));
            }
        } catch (error) {
            console.error('Error generating invoice number:', error);
            const timestamp = Date.now().toString().slice(-6);
            const shopPrefix = currentShop.shop_id.toString().slice(-3);
            setFormData(prev => ({
                ...prev,
                invoice_number: `INV-${shopPrefix}-${timestamp}`
            }));
        }
    }, [currentShop?.shop_id, user?.username]);

    useEffect(() => {
        if (currentShop?.shop_id && !isEditMode) {
            generateInvoiceNumber();
        }
    }, [currentShop?.shop_id, isEditMode, generateInvoiceNumber]);

    useEffect(() => {
        if (invoiceId) {
            setIsEditMode(true);
            loadInvoiceData(invoiceId);
        }
    }, [invoiceId]);

    const loadInvoiceData = async (id) => {
        if (!currentShop?.shop_id || !user?.username) return;

        setIsLoading(true);
        try {
            // FIX: Using fetchApi
            const data = await fetchApi({
                action: 'get_record',
                id: id,
                shop_id: currentShop.shop_id,
                username: user.username
            });

            if (data.status === 'success' && data.data) {
                const invoice = data.data;
                let pictures = [];
                
                if (Array.isArray(invoice.bill_pictures)) {
                    pictures = invoice.bill_pictures;
                } else if (invoice.bill_pictures) {
                    try {
                        pictures = JSON.parse(invoice.bill_pictures);
                    } catch (e) {
                        pictures = [];
                    }
                }

                setFormData({
                    invoice_number: invoice.invoice_number || '',
                    customer_name: invoice.customer_name || '',
                    bill_number: invoice.bill_number || '',
                    bill_pictures: pictures || [],
                    username: invoice.username || user.username,
                    shop_id: invoice.shop_id || currentShop.shop_id
                });
            } else {
                showToast('error', data.message || 'Failed to load invoice');
            }
        } catch (error) {
            console.error('Error loading invoice:', error);
            showToast('error', error.message || translations?.networkError || 'Network error');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        const loadTranslations = async (lang) => {
            const trans = await invoiceTranslator.getTranslations(lang);
            setTranslations(trans);
        };

        const handleLanguageChange = () => {
            const lang = localStorage.getItem('ourzest_language') || 'en';
            if (lang !== currentLanguage) {
                setCurrentLanguage(lang);
                loadTranslations(lang);
            }
        };

        const handleCustomLanguageChange = (event) => {
            if (event.detail && event.detail.language) {
                const newLang = event.detail.language;
                if (newLang !== currentLanguage) {
                    setCurrentLanguage(newLang);
                    loadTranslations(newLang);
                }
            }
        };

        loadTranslations(currentLanguage);

        window.addEventListener('storage', handleLanguageChange);
        window.addEventListener('languageChanged', handleCustomLanguageChange);

        const interval = setInterval(() => {
            const storedLang = localStorage.getItem('ourzest_language') || 'en';
            if (storedLang !== currentLanguage) {
                setCurrentLanguage(storedLang);
                loadTranslations(storedLang);
            }
        }, 1000);

        return () => {
            window.removeEventListener('storage', handleLanguageChange);
            window.removeEventListener('languageChanged', handleCustomLanguageChange);
            clearInterval(interval);
        };
    }, [currentLanguage]);

    const handleAddPicture = useCallback((imageDataUrl) => {
        setFormData(prev => ({
            ...prev,
            bill_pictures: [...prev.bill_pictures, imageDataUrl]
        }));
    }, []);

    const handleRemovePicture = useCallback((index) => {
        setFormData(prev => ({
            ...prev,
            bill_pictures: prev.bill_pictures.filter((_, i) => i !== index)
        }));
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.invoice_number?.trim()) {
            newErrors.invoice_number = 'Invoice number is required';
        }

        if (!currentShop?.shop_id) {
            newErrors.shop_id = 'Shop is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isSavingRef.current) {
            showToast('warning', 'Please wait, invoice is already being processed...');
            return;
        }

        if (!currentShop?.shop_id) {
            showToast('error', 'No shop selected. Please select a shop first.');
            return;
        }

        if (!validateForm()) {
            showToast('error', 'Please fix the errors in the form');
            return;
        }

        isSavingRef.current = true;
        setSaving(true);

        try {
            const username = user?.username || 'unknown';
            const action = isEditMode ? 'edit' : 'add';

            const payload = {
                action: action,
                invoice_number: formData.invoice_number,
                customer_name: formData.customer_name || '',
                bill_number: formData.bill_number || '',
                bill_pictures: formData.bill_pictures,
                shop_id: currentShop.shop_id,
                username: username
            };

            if (isEditMode && invoiceId) {
                payload.id = invoiceId;
            }

            // FIX: Using fetchApi
            const data = await fetchApi(payload);

            if (data.status === 'success') {
                showToast('success',
                    isEditMode ? (translations?.invoiceUpdated || 'Invoice updated successfully!')
                        : (translations?.invoiceSaved || 'Invoice saved successfully!')
                );

                if (!isEditMode) {
                    setFormData({
                        ...initialFormData,
                        shop_id: currentShop.shop_id,
                        username: username
                    });
                    generateInvoiceNumber();
                }

                if (onSuccess) {
                    onSuccess(data.data?.id || invoiceId);
                }
            } else {
                showToast('error', data.message || (isEditMode ? translations?.failedUpdate : translations?.failedSave) || 'Failed to save invoice');
            }
        } catch (error) {
            console.error('Submit error:', error);
            showToast('error', error.message || translations?.networkError || 'Network error occurred');
        } finally {
            isSavingRef.current = false;
            setSaving(false);
        }
    };

    const handleClear = () => {
        if (isEditMode) {
            setIsEditMode(false);
        }
        setFormData({
            ...initialFormData,
            shop_id: currentShop?.shop_id || null,
            username: user?.username || ''
        });
        setErrors({});
        generateInvoiceNumber();
        showToast('success', 'Form cleared');

        setTimeout(() => {
            if (invoiceNumberRef.current) {
                invoiceNumberRef.current.focus();
            }
        }, 100);
    };

    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

    if (isLoading) {
        return (
            <div className={`ozui-container ${isRTL ? 'ozui-rtl' : 'ozui-ltr'} ${darkMode ? 'ozui-dark' : ''}`}>
                <div className="ozui-loading-state">
                    <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                    <p>{translations?.loading || 'Loading...'}</p>
                </div>
            </div>
        );
    }

    return (
        <div className={`ozui-container ${isRTL ? 'ozui-rtl' : 'ozui-ltr'} ${darkMode ? 'ozui-dark' : ''}`}>
            <ToastNotification toast={toast} setToast={setToast} translations={translations} />

            <div className="ozui-sticky-header">
                <div className="ozui-header-bg-animation">
                    <div className="gradient-orb orb-1"></div>
                    <div className="gradient-orb orb-2"></div>
                    <div className="gradient-orb orb-3"></div>
                    <div className="gradient-orb orb-4"></div>
                    <div className="gradient-orb orb-5"></div>
                </div>
                <div className="ozui-header-content">
                    <div className="ozui-header-left">
                        <div className="ozui-header-title">
                            <h1>
                                {isEditMode ? (translations?.editInvoice || 'Edit Invoice') : (translations?.newInvoice || 'New Invoice')}
                            </h1>
                        </div>
                    </div>

                    <div className="ozui-header-right">
                        <div className="ozui-header-actions">
                            <button
                                className="ozui-action-btn clear-draft"
                                onClick={handleClear}
                                disabled={saving}
                                title={translations?.clear || 'Clear'}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                                <span>{translations?.clear || 'Clear'}</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="ozui-form">
                <div className="ozui-form-row">
                    <div className={`ozui-field ${errors.invoice_number ? 'ozui-error' : ''}`}>
                        <div className="ozui-input-wrapper">
                            <input
                                ref={invoiceNumberRef}
                                type="text"
                                name="invoice_number"
                                id="invoice_number"
                                value={formData.invoice_number}
                                onChange={handleChange}
                                placeholder=" "
                                disabled={saving || isEditMode}
                                readOnly={!isEditMode}
                                className={!isEditMode ? 'ozui-auto-generated' : ''}
                            />
                            <label htmlFor="invoice_number">
                                <FontAwesomeIcon icon={faFileInvoice} />
                                {translations?.invoiceNumber || 'Invoice Number'} <span>*</span>
                            </label>
                        </div>
                        {errors.invoice_number && <small>{errors.invoice_number}</small>}
                    </div>

                    <div className="ozui-field">
                        <div className="ozui-input-wrapper">
                            <input
                                type="text"
                                name="bill_number"
                                id="bill_number"
                                value={formData.bill_number}
                                onChange={handleChange}
                                placeholder=" "
                                disabled={saving}
                            />
                            <label htmlFor="bill_number">
                                <FontAwesomeIcon icon={faTag} />
                                {translations?.billNumber || 'Bill Number'}
                            </label>
                        </div>
                    </div>
                </div>

                <div className="ozui-form-row">
                    <div className="ozui-field ozui-full-width">
                        <div className="ozui-scanner-section">
                            <label className="ozui-scanner-label">
                                <FontAwesomeIcon icon={faImage} />
                                {translations?.billPictures || 'Bill Pictures'}
                                <span className="ozui-picture-count">
                                    ({formData.bill_pictures.length})
                                </span>
                            </label>
                            <BillPicturesGallery
                                pictures={formData.bill_pictures}
                                onRemove={handleRemovePicture}
                                onAdd={handleAddPicture}
                                translations={translations}
                            />
                        </div>
                    </div>
                </div>

                <div className="ozui-form-actions">
                    <button
                        type="submit"
                        className="ozui-btn ozui-btn-primary"
                        disabled={saving || isLoading || !currentShop}
                    >
                        {saving ? (
                            <>
                                <FontAwesomeIcon icon={faSpinner} spin />
                                <span>{translations?.saving || 'Saving...'}</span>
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faSave} />
                                <span>{isEditMode ? (translations?.updateInvoice || 'Update Invoice') : (translations?.saveInvoice || 'Save Invoice')}</span>
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default PendingInvoices;
